import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Swords, MonitorPlay, ArrowRight, Radio, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";

const T = {
  fr: { eyebrow: "Le rendez-vous du mois", debate: "Le débat du mois", conf: "La conférence du mois", vote: "Donner mon avis", watch: "Voir la conférence", live: "En direct" },
  en: { eyebrow: "This month's rendez-vous", debate: "Debate of the month", conf: "Conference of the month", vote: "Cast my vote", watch: "See the conference", live: "Live" },
  es: { eyebrow: "La cita del mes", debate: "El debate del mes", conf: "La conferencia del mes", vote: "Dar mi opinión", watch: "Ver la conferencia", live: "En directo" },
  nl: { eyebrow: "De afspraak van de maand", debate: "Het debat van de maand", conf: "De conferentie van de maand", vote: "Mijn mening geven", watch: "Bekijk de conferentie", live: "Live" },
};

export function MonthShortcuts() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = T[lang] || T.fr;
  const [debate, setDebate] = useState(null);
  const [conf, setConf] = useState(null);

  useEffect(() => {
    api.get(`/debates/arena`, { params: { lang } }).then(({ data }) => setDebate((data.items || [])[0] || null)).catch(() => {});
    api.get(`/conference/current`, { params: { lang } }).then(({ data }) => setConf(data && data.title ? data : null)).catch(() => {});
  }, [lang]);

  if (!debate && !conf) return null;
  const isLive = conf && conf.live && conf.live.status === "live";

  return (
    <section className="relative px-6 my-10" data-testid="month-shortcuts">
      <div className="max-w-6xl mx-auto">
        <p className="text-center text-primary/80 tracking-[0.25em] uppercase text-xs mb-6">{c.eyebrow}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {debate && (
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <Link
                to={`/univers/l-agora?debat=${encodeURIComponent(debate.id)}#debat-du-mois`}
                data-testid="shortcut-debate"
                className="group relative flex flex-col h-full rounded-2xl border border-primary/20 glass p-6 transition-colors hover:border-primary/45"
              >
                <div className="flex items-center gap-2 text-primary/85 text-[11px] uppercase tracking-[0.22em] mb-3">
                  <Swords size={15} /> {c.debate}
                </div>
                <p className="font-display text-foreground/90 text-xl sm:text-2xl leading-snug flex-1" data-testid="shortcut-debate-title">
                  {debate.question}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                  {c.vote}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </motion.div>
          )}

          {conf && (
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.08 }}>
              <Link
                to="/univers/l-agora#les-conferences"
                data-testid="shortcut-conference"
                className="group relative flex flex-col h-full rounded-2xl border border-primary/20 glass p-6 transition-colors hover:border-primary/45"
              >
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="inline-flex items-center gap-2 text-primary/85 text-[11px] uppercase tracking-[0.22em]">
                    <MonitorPlay size={15} /> {c.conf}
                  </span>
                  {isLive ? (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-red-300 bg-red-500/15 border border-red-400/40 rounded-full px-2.5 py-1">
                      <Radio size={11} className="animate-pulse" /> {c.live}
                    </span>
                  ) : conf.month ? (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-foreground/55">
                      <CalendarClock size={12} /> {conf.month}
                    </span>
                  ) : null}
                </div>
                <p className="font-display text-foreground/90 text-xl sm:text-2xl leading-snug" data-testid="shortcut-conference-title">
                  {conf.title}
                </p>
                {conf.subtitle && <p className="mt-1.5 text-foreground/55 text-sm leading-snug line-clamp-2 flex-1">{conf.subtitle}</p>}
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                  {c.watch}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
